import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { sampleByEvent } from "posthog-js/lib/src/customizations";
import { ENVIRONMENT, SENTRY_DSN, POSTHOG_API_KEY } from "./config";

let isSentryInitialized = false;

const initSentry = () => {
  if (isSentryInitialized || !SENTRY_DSN || ENVIRONMENT !== "production") return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 0.1,
  });

  isSentryInitialized = true;
};

let isPosthogInitialized = false;

const initPosthog = () => {
  if (isPosthogInitialized || ENVIRONMENT !== "production") return;

  posthog.init(POSTHOG_API_KEY, {
    api_host: "https://eu.i.posthog.com",
    autocapture: false,
    capture_pageview: false,
    person_profiles: "identified_only",
    before_send: sampleByEvent(["$web_vitals"], 0.1),
    __add_tracing_headers: ["beta.api.querco.co", "api.querco.co"],
  });

  posthog.register({ source: "application" });
  isPosthogInitialized = true;
};

const purgePosthogStorage = () => {
  [localStorage, sessionStorage].forEach((storage) => {
    Object.keys(storage)
      .filter((key) => key.startsWith("ph_"))
      .forEach((key) => storage.removeItem(key));
  });

  const parentDomain = location.hostname.split(".").slice(-2).join(".");
  document.cookie.split(";").forEach((cookie) => {
    const name = cookie.split("=")[0].trim();
    if (!name.startsWith("ph_")) return;
    document.cookie = `${name}=; max-age=0; path=/`;
    document.cookie = `${name}=; max-age=0; path=/; domain=.${parentDomain}`;
  });
};

const applyPosthogConsent = (accepted) => {
  if (!accepted) {
    if (isPosthogInitialized) posthog.opt_out_capturing();
    purgePosthogStorage();
    return;
  }

  initPosthog();
  if (isPosthogInitialized && posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
};

const syncToSentry = (userId, indexName) => {
  if (!isSentryInitialized) return;

  Sentry.setUser({ id: userId });
  if (indexName) Sentry.setTag("indexName", indexName);
};

const identify = (id, indexName) => {
  if (!isPosthogInitialized) return syncToSentry(id, indexName);

  const user = posthog.get_distinct_id();
  const groups = posthog.getGroups();

  if (user === id && groups?.indexName === indexName) return;

  posthog.identify(id);
  posthog.group("indexName", indexName);
  syncToSentry(id, indexName);
};

const toError = (error) => {
  if (error instanceof Error) return error;
  if (typeof error === "string") return new Error(error);
  const normalized = new Error(error?.message || error?.code || "Unknown error");
  if (error?.code) normalized.code = error.code;
  return normalized;
};

const EXPECTED_CODES = ["ADMISSION_LOCKED", "PATIENT_NOT_FOUND"];

const capture = (error) => {
  if (EXPECTED_CODES.includes(error?.code)) return;

  console.error(error);

  if (!isSentryInitialized) return;

  const sessionId = isPosthogInitialized ? posthog.get_session_id?.() : undefined;
  const normalized = toError(error);

  Sentry.withScope((scope) => {
    if (sessionId) scope.setTag("posthog_session_id", sessionId);
    if (normalized.code) scope.setTag("error_code", normalized.code);
    if (normalized !== error) scope.setFingerprint([normalized.code || normalized.message]);
    Sentry.captureException(normalized);
  });
};

const track = (event, properties = {}) => {
  if (!isPosthogInitialized) return;
  posthog.capture(event, properties);
};

export { posthog, initSentry, applyPosthogConsent, capture, track, identify };
